import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import ChildCard from '@/components/shared/ChildCard';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CompanyInfoData {
  companyName: string;
  country: string;
  city: string;
  addresses: string;
  postCode: string;
}

const CompanyInfoPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [companyInfo, setCompanyInfo] = useState<CompanyInfoData>({
    companyName: '',
    country: '',
    city: '',
    addresses: '',
    postCode: '',
  });

  // Load company info data
  useEffect(() => {
    const loadCompanyInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getCompanyInfo();
        
        if (data === null) {
          // Data doesn't exist - we'll create it
          setIsCreating(true);
        } else {
          // Data exists - fill form and we'll update it
          setCompanyInfo({
            companyName: data.companyName || '',
            country: data.country || '',
            city: data.city || '',
            addresses: data.addresses || '',
            postCode: data.postCode?.toString() || '',
          });
          setIsCreating(false);
        }
      } catch (err: any) {
        console.error('Error loading company info:', err);
        setError(err.message || t('companyInfo.errors.loading'));
      } finally {
        setLoading(false);
      }
    };

    loadCompanyInfo();
  }, [t]);

  const handleChange = (field: keyof CompanyInfoData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!companyInfo.companyName.trim()) {
      setError(t('companyInfo.errors.companyNameRequired'));
      return;
    }
    if (!companyInfo.country.trim()) {
      setError(t('companyInfo.errors.countryRequired'));
      return;
    }
    if (!companyInfo.city.trim()) {
      setError(t('companyInfo.errors.cityRequired'));
      return;
    }
    if (!companyInfo.addresses.trim()) {
      setError(t('companyInfo.errors.addressesRequired'));
      return;
    }
    if (!companyInfo.postCode.trim()) {
      setError(t('companyInfo.errors.postCodeRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isCreating) {
        await apiService.createCompanyInfo({
          companyName: companyInfo.companyName.trim(),
          country: companyInfo.country.trim(),
          city: companyInfo.city.trim(),
          addresses: companyInfo.addresses.trim(),
          postCode: companyInfo.postCode.trim(),
        });
        setIsCreating(false);
      } else {
        await apiService.updateCompanyInfo({
          companyName: companyInfo.companyName.trim(),
          country: companyInfo.country.trim(),
          city: companyInfo.city.trim(),
          addresses: companyInfo.addresses.trim(),
          postCode: companyInfo.postCode.trim(),
        });
      }

      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Error saving company info:', err);
      setError(err.message || t('companyInfo.errors.saving'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('menu.Company Info')}
      </Typography>

      {error && (
        <Alert
          variant="filled"
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <ChildCard>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('companyInfo.companyName')}
              value={companyInfo.companyName}
              onChange={handleChange('companyName')}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('companyInfo.country')}
              value={companyInfo.country}
              onChange={handleChange('country')}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('companyInfo.city')}
              value={companyInfo.city}
              onChange={handleChange('city')}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('companyInfo.addresses')}
              value={companyInfo.addresses}
              onChange={handleChange('addresses')}
              variant="outlined"
              fullWidth
              required
              multiline
              rows={3}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('companyInfo.postCode')}
              value={companyInfo.postCode}
              onChange={handleChange('postCode')}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                disabled={saving}
                sx={{ minWidth: '120px' }}
              >
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('companyInfo.save')
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </ChildCard>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('companyInfo.success')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyInfoPage;
